// Carer-parity S3b — bell button rendered in carer page headers.
// Reads the carer-notifications context (owned by CarerLayout) and
// exposes a single click target that opens the shared
// NotificationsDrawer. Unread count overlays as a small red badge,
// capped at 9+ to mirror the coordinator chrome (Shell.tsx:101-104).
//
// Tap target ≥44×44 (`p-2` ≈ 8px on each side around the 24px svg)
// matches mobile-first guidance (DESIGN-SYSTEM.md §Touch targets).

import type { JSX } from "react";
import { useCarerNotifications } from "./CarerNotificationsContext.js";

function BellIcon(): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );
}

export function CarerNotificationBell(): JSX.Element {
  const { unreadCount, openDrawer } = useCarerNotifications();
  const ariaLabel = unreadCount > 0
    ? `Notifications (${unreadCount} unread)`
    : "Notifications";
  return (
    <button
      type="button"
      onClick={openDrawer}
      aria-label={ariaLabel}
      data-testid="carer-bell"
      className="relative p-2 rounded-full text-navy-200 hover:text-white hover:bg-navy-700/40 transition-colors"
    >
      <BellIcon />
      {unreadCount > 0 && (
        <span
          data-testid="carer-bell-badge"
          className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
        >
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}
    </button>
  );
}
